import { useMemo } from 'react';
import { useField } from 'formik';
import { useSelector } from 'react-redux';
import {
  Col,
  Row,
  Dialog,
  InputField,
  RadioFieldGroup,
} from '@gv/triage-components';

import { Config } from 'config';
import { RootState } from 'store';
import { isAcceptedAppointment } from 'utils/helpers';
import { useDetailsQuery } from 'store/api/hospitals';
import { Administrative } from 'components/cases/fields/actions';
import {
  useFormEntities,
  callOutcomeModel,
  CheckProtocolsInfo,
  CorporateOptionsProps,
} from 'components/cases/form';
import {
  CallOutcomeType,
  CallOutcomeEmergency,
  CallOutcomeAppointment,
  CallOutcomeTransferToBackline,
} from 'types';
import {
  CallOutcomeEmergencyTitle,
  CallOutcomeAppointmentTitle,
  CallOutcomeTransferToBacklineTitle,
} from 'types/data';

import { DepartmentSelect } from './department-select';

export const CallOutcomeAction = ({
  hideNotes,
  permissions,
  corporateOptions,
  isCorporateFetching,
}: CorporateOptionsProps & { hideNotes?: boolean }) => {
  const { hospital, hospitalId } = useFormEntities();
  const [type] = useField(callOutcomeModel.type);
  const [modalField, , modalHelpers] = useField('isWarningModalShown');
  const [emergencyTypeField] = useField(callOutcomeModel.emergency.type);
  const {
    hideCallOutcomeTypes,
    blockedCallOutcomeTypes,
    hospitalCallOutcomeTitles,
  } = useMemo(
    () => ({
      hideCallOutcomeTypes: Config.helpers.getHideCallOutcomeTypes(hospitalId),
      hospitalCallOutcomeTitles:
        Config.helpers.getCallOutcomeTitles(hospitalId),
      blockedCallOutcomeTypes:
        Config.helpers.getBlockedCallOutcomeTypes(hospitalId),
    }),
    [hospitalId]
  );

  const hospitalQuery = useDetailsQuery(String(hospitalId), {
    skip: !hospitalId,
  });

  const hasAccessToItem = (value: string): boolean =>
    Boolean(hospitalId && !blockedCallOutcomeTypes?.includes(value));

  const onModalClose = () => {
    modalHelpers.setValue(true);
  };

  const { isShowPlanForCVT } = useSelector(
    (state: RootState) => state.analyticsExport
  );

  const isEmergency = type.value === CallOutcomeType.Emergency;
  const isAppointment = type.value === CallOutcomeType.Appointment;

  const appointmentItems = useMemo(() => {
    const appointmentOutcome =
      hospitalQuery?.data?.hospital_calloutcome?.call_outcome?.find(
        (o) => o.outcome === CallOutcomeType.Appointment
      );

    if (!appointmentOutcome) {
      return [];
    }

    return appointmentOutcome.subCallOutcome.map((sub) => ({
      value: sub,
      label: CallOutcomeAppointmentTitle[sub as CallOutcomeAppointment],
    }));
  }, [hospitalQuery?.data?.hospital_calloutcome?.call_outcome]);

  const emergencyItems = useMemo(() => {
    const emergencyOutcome =
      hospitalQuery?.data?.hospital_calloutcome?.call_outcome?.find(
        (o) => o.outcome === CallOutcomeType.Emergency
      );

    if (!emergencyOutcome) {
      return [];
    }

    return emergencyOutcome.subCallOutcome.map((sub) => ({
      value: sub,
      label: CallOutcomeEmergencyTitle[sub as CallOutcomeEmergency],
    }));
  }, [hospitalQuery?.data?.hospital_calloutcome?.call_outcome]);

  return (
    <>
      {isAppointment && (
        <div>
          <RadioFieldGroup
            label="Select option"
            name={callOutcomeModel.appointment.type}
            items={
              appointmentItems?.length > 0
                ? appointmentItems
                : permissions?.appointmentTab && isShowPlanForCVT
                  ? [
                      {
                        value: CallOutcomeAppointment.Scheduled,
                        label:
                          CallOutcomeAppointmentTitle[
                            CallOutcomeAppointment.Scheduled
                          ],
                        hasAccess: isAcceptedAppointment(
                          CallOutcomeAppointment.Scheduled,
                          hospital
                        ),
                      },
                      {
                        value: CallOutcomeAppointment.Recommended,
                        label:
                          CallOutcomeAppointmentTitle[
                            CallOutcomeAppointment.Recommended
                          ],
                        hasAccess:
                          hasAccessToItem(CallOutcomeAppointment.Recommended) &&
                          isAcceptedAppointment(
                            CallOutcomeAppointment.Recommended,
                            hospital
                          ),
                      },
                      {
                        value: CallOutcomeAppointment.Cancelled,
                        label:
                          hospitalCallOutcomeTitles?.[
                            CallOutcomeAppointment.Cancelled
                          ] ??
                          CallOutcomeAppointmentTitle[
                            CallOutcomeAppointment.Cancelled
                          ],
                      },
                      {
                        value: CallOutcomeAppointment.Declined,
                        label:
                          CallOutcomeAppointmentTitle[
                            CallOutcomeAppointment.Declined
                          ],
                      },
                      {
                        value: CallOutcomeAppointment.Rescheduled,
                        label:
                          CallOutcomeAppointmentTitle[
                            CallOutcomeAppointment.Rescheduled
                          ],
                        hasAccess:
                          hasAccessToItem(CallOutcomeAppointment.Rescheduled) &&
                          isAcceptedAppointment(
                            CallOutcomeAppointment.Rescheduled,
                            hospital
                          ),
                      },
                    ]
                  : []
            }
          />
        </div>
      )}

      {isEmergency && (
        <div>
          <RadioFieldGroup
            label="Refer to"
            name={callOutcomeModel.emergency.type}
            items={
              emergencyItems?.length > 0
                ? emergencyItems
                : [
                    {
                      value: CallOutcomeEmergency.ReferToOnCall,
                      label:
                        CallOutcomeEmergencyTitle[
                          CallOutcomeEmergency.ReferToOnCall
                        ],
                      hasAccess:
                        hasAccessToItem(CallOutcomeEmergency.ReferToOnCall) &&
                        hospital &&
                        hospital.speciality !== 'Emergency' &&
                        !hideCallOutcomeTypes?.includes(
                          CallOutcomeEmergency.ReferToOnCall
                        ) &&
                        isShowPlanForCVT,
                    },
                    {
                      value: CallOutcomeEmergency.ReferToEr,
                      label:
                        CallOutcomeEmergencyTitle[
                          CallOutcomeEmergency.ReferToEr
                        ],
                      hasAccess:
                        hospital &&
                        hospital.speciality !== 'Emergency' &&
                        isShowPlanForCVT,
                    },
                    {
                      value: CallOutcomeEmergency.ReferToPPH,
                      label:
                        hospitalCallOutcomeTitles?.[
                          CallOutcomeEmergency.ReferToPPH
                        ] ??
                        CallOutcomeEmergencyTitle[
                          CallOutcomeEmergency.ReferToPPH
                        ],
                      hasAccess:
                        hospital &&
                        hospital.speciality !== 'Emergency' &&
                        !hideCallOutcomeTypes?.includes(
                          CallOutcomeEmergency.ReferToOnCall
                        ) &&
                        isShowPlanForCVT,
                    },
                    {
                      value: CallOutcomeEmergency.Inbound,
                      hasAccess:
                        hospital &&
                        (hospital.speciality === 'Emergency' ||
                          hospital.speciality === 'Emergency & Specialty'),
                      label:
                        hospitalCallOutcomeTitles?.[
                          CallOutcomeEmergency.Inbound
                        ] ??
                        CallOutcomeEmergencyTitle[CallOutcomeEmergency.Inbound],
                    },

                    {
                      value: CallOutcomeEmergency.ClientDeclined,
                      hasAccess:
                        hospital &&
                        (hospital.speciality === 'Emergency' ||
                          hospital.speciality === 'Emergency & Specialty'),
                      label:
                        hospitalCallOutcomeTitles?.[
                          CallOutcomeEmergency.ClientDeclined
                        ] ??
                        CallOutcomeEmergencyTitle[
                          CallOutcomeEmergency.ClientDeclined
                        ],
                    },
                    {
                      value: CallOutcomeEmergency.PossiblyInbound,
                      hasAccess:
                        hospital &&
                        (hospital.speciality === 'Emergency' ||
                          hospital.speciality === 'Emergency & Specialty') &&
                        isShowPlanForCVT,
                      label:
                        hospitalCallOutcomeTitles?.[
                          CallOutcomeEmergency.PossiblyInbound
                        ] ??
                        CallOutcomeEmergencyTitle[
                          CallOutcomeEmergency.PossiblyInbound
                        ],
                    },
                    {
                      hasAccess: isShowPlanForCVT,
                      value: CallOutcomeEmergency.ReferToAnotherER,
                      label:
                        hospitalCallOutcomeTitles?.[
                          CallOutcomeEmergency.ReferToAnotherER
                        ] ??
                        CallOutcomeEmergencyTitle[
                          CallOutcomeEmergency.ReferToAnotherER
                        ],
                    },
                  ]
            }
          />
          {emergencyTypeField.value ===
            CallOutcomeEmergency.PossiblyInbound && (
            <InputField
              label="Notes"
              type="textarea-autosize"
              name="possibly_inbound_notes"
              placeholder="Possibly Inbound"
            />
          )}
        </div>
      )}

      {type.value === CallOutcomeType.Administrative && isShowPlanForCVT && (
        <>
          <DepartmentSelect
            isDisabled={isCorporateFetching}
            options={corporateOptions?.call}
            name={callOutcomeModel.administrative.call.corporateHospitalId}
          />

          <CheckProtocolsInfo hideNotes={hideNotes}>
            <Administrative
              hospitalId={hospitalId || ''}
              name={callOutcomeModel.administrative.call.outcomes}
            />
          </CheckProtocolsInfo>
        </>
      )}

      {type.value === CallOutcomeType.TransferToBackline &&
        isShowPlanForCVT && (
          <div>
            <Row>
              <Col>
                <RadioFieldGroup
                  label="Select options"
                  name={callOutcomeModel.transferToBackline.type}
                  items={Object.values(CallOutcomeTransferToBackline).map(
                    (value) => ({
                      value,
                      label:
                        hospitalCallOutcomeTitles?.[value] ??
                        CallOutcomeTransferToBacklineTitle[value],
                    })
                  )}
                />
              </Col>
            </Row>
            <InputField
              label="Notes"
              placeholder="Add note"
              name="additional_notes"
              type="textarea-autosize"
            />
          </div>
        )}

      <Dialog
        title="Please note:"
        onClose={onModalClose}
        onConfirm={onModalClose}
        closeOnDocumentClick={false}
        open={!modalField.value && (isEmergency || isAppointment)}
        text="Unless the pet owner has an administrative issue, we should recommend an appointment or refer the case to the ER. Please explain to the pet owner what the complications could be without a physical exam and why it is recommended."
      />
    </>
  );
};
